/**
 * Integrations → WhatsApp (Twilio) — Phase 1.
 *
 * Tabs:
 *   - Contacts: list, upsert, role tags
 *   - Default Subscriptions: read-only matrix (role_tag × notification_type)
 *   - Resolved view: which contacts will receive a given notification (preview)
 *   - Send Test Alert: pick contacts + notification type → real WhatsApp send
 *   - Recent Messages: outbound + inbound audit log with delivery status
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { whatsappAPI } from '../utils/api';
import {
  MessageCircle, Plus, Send, RefreshCw, Trash2, AlertCircle, CheckCircle2, XCircle, Sparkles, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_COLORS = {
  ops_oncall:  'bg-emerald-100 text-emerald-800',
  ops_manager: 'bg-blue-100 text-blue-800',
  management:  'bg-purple-100 text-purple-800',
  site_owner:  'bg-amber-100 text-amber-800',
};
const STATUS_COLORS = {
  queued:      'bg-slate-100 text-slate-700',
  sent:        'bg-blue-100 text-blue-800',
  delivered:   'bg-emerald-100 text-emerald-800',
  read:        'bg-emerald-200 text-emerald-900',
  received:    'bg-indigo-100 text-indigo-800',
  failed:      'bg-red-100 text-red-800',
  undelivered: 'bg-red-100 text-red-800',
};
const NOTIF_LABELS = {
  power_outage: 'Power outage',
  soc_50: 'SoC < 50%',
  soc_20: 'SoC < 20% (critical)',
  daily_8am: 'Daily brief 8 AM',
  daily_8pm: 'Daily brief 8 PM',
};

export const WhatsAppIntegrationPage = () => {
  const [tab, setTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContact, setShowContact] = useState(null);  // null | {} | contact
  const [showTest, setShowTest] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, m] = await Promise.all([
        whatsappAPI.listContacts(),
        whatsappAPI.defaultPolicy(),
        whatsappAPI.messages(50),
      ]);
      setContacts(c.data?.rows || []);
      setPolicy(p.data);
      setMessages(m.data?.rows || []);
    } catch (e) { toast.error('Failed to load WhatsApp data'); }
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const seedDefaults = async () => {
    try { await whatsappAPI.seed(); toast.success('Seeded UD/PS/SM'); reload(); }
    catch (e) { toast.error('Seed failed'); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="whatsapp-integration-page">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 md:p-8 max-w-[1500px] mx-auto space-y-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <PageHeader title="WhatsApp (Twilio)" description="Ops alerts + two-way ACK / RESOLVE / STATUS" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={seedDefaults} data-testid="seed-btn"><Sparkles className="w-3.5 h-3.5 mr-1" />Seed UD/PS/SM</Button>
              <Button size="sm" onClick={() => setShowTest(true)} data-testid="open-send-test"><Send className="w-3.5 h-3.5 mr-1" />Send Test Alert</Button>
            </div>
          </div>

          {/* Sandbox-mode caveat */}
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex gap-2 text-xs text-amber-900">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" />
            <div>
              <b>Sandbox mode active.</b> The sender is Twilio's shared sandbox number <code className="px-1.5 py-0.5 bg-amber-100 rounded">+1 415 523 8886</code>. Each recipient must WhatsApp <code className="px-1.5 py-0.5 bg-amber-100 rounded">join &lt;your-sandbox-code&gt;</code> to that number ONCE before they can receive any alerts. Get the join code from <a href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console → Messaging → Try WhatsApp</a>. When you upgrade to a registered WhatsApp Business sender, this restriction goes away.
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-200 flex gap-1 -mb-px" role="tablist">
            {[
              { k: 'contacts', label: `Contacts (${contacts.length})` },
              { k: 'policy',   label: 'Default Subscriptions' },
              { k: 'resolve',  label: 'Resolved Recipients' },
              { k: 'messages', label: `Recent Messages (${messages.length})` },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                className={`px-3 py-1.5 text-xs border-b-2 transition-colors ${tab === t.k ? 'border-slate-900 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {loading ? <LoadingSpinner /> : (
            <>
              {tab === 'contacts' && <ContactsView contacts={contacts} onEdit={setShowContact} onReload={reload} />}
              {tab === 'policy'   && policy && <DefaultPolicyView policy={policy} />}
              {tab === 'resolve'  && <ResolveView />}
              {tab === 'messages' && <MessagesView rows={messages} />}
            </>
          )}
        </div>
      </div>

      {showContact !== null && <ContactModal initial={showContact} onClose={() => setShowContact(null)} onSaved={() => { setShowContact(null); reload(); }} />}
      {showTest && <SendTestModal contacts={contacts} onClose={() => setShowTest(false)} onSent={() => { setShowTest(false); reload(); }} />}
    </div>
  );
};

// ─── Contacts ──
function ContactsView({ contacts, onEdit, onReload }) {
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name} (${c.phone_e164})? Their subscriptions will also be removed.`)) return;
    try { await whatsappAPI.deleteContact(c.contact_id); toast.success('Deleted'); onReload(); }
    catch (e) { toast.error('Delete failed'); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="p-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Internal-only WhatsApp recipients</h3>
        <Button size="sm" onClick={() => onEdit({})} data-testid="add-contact-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Contact</Button>
      </div>
      {contacts.length === 0 ? (
        <div className="p-8 text-center text-xs text-slate-400">No contacts yet. Click <b>Seed UD/PS/SM</b> above or <b>Add Contact</b>.</div>
      ) : (
        <table className="w-full text-xs" data-testid="contacts-table">
          <thead><tr className="bg-slate-50 text-slate-500">
            <th className="px-4 py-2 text-left font-medium">Name</th>
            <th className="px-4 py-2 text-left font-medium">Phone</th>
            <th className="px-4 py-2 text-left font-medium">Role tags</th>
            <th className="px-4 py-2 text-left font-medium">Status</th>
            <th className="px-4 py-2 text-left font-medium"></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-50">
            {contacts.map(c => (
              <tr key={c.contact_id} data-testid={`contact-row-${c.contact_id}`} className="hover:bg-slate-50/50">
                <td className="px-4 py-2 font-medium">{c.name}</td>
                <td className="px-4 py-2 font-mono">{c.phone_e164}</td>
                <td className="px-4 py-2">
                  {(c.role_tags || []).map(t => <span key={t} className={`text-[10px] px-1.5 py-0.5 rounded mr-1 ${ROLE_COLORS[t] || 'bg-slate-100 text-slate-700'}`}>{t}</span>)}
                </td>
                <td className="px-4 py-2 text-[11px]">
                  <span className={c.is_active ? 'text-emerald-700' : 'text-slate-400'}>{c.is_active ? '● active' : '○ inactive'}</span>
                  <span className={`ml-2 ${c.opt_in_status ? 'text-emerald-700' : 'text-amber-700'}`}>{c.opt_in_status ? 'opted-in' : 'NOT opted-in'}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => onEdit(c)} className="text-slate-500 hover:text-slate-900 mr-3 text-[11px] underline" data-testid={`edit-${c.contact_id}`}>edit</button>
                  <button onClick={() => remove(c)} className="text-red-500 hover:text-red-700" data-testid={`delete-${c.contact_id}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Default policy matrix ──
function DefaultPolicyView({ policy }) {
  const tags = policy.role_tags;
  const types = policy.notification_types;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="p-3 border-b border-slate-100 text-xs text-slate-600">
        Role-tag × notification-type defaults. A contact receives an alert when <b>any</b> of their role tags has the corresponding cell ticked. Per-site overrides (added on the Site → Notifications panel — Phase 2) take precedence.
      </div>
      <table className="w-full text-xs" data-testid="policy-matrix">
        <thead><tr className="bg-slate-50 text-slate-500">
          <th className="px-4 py-2 text-left font-medium">Role tag</th>
          {types.map(t => <th key={t} className="px-4 py-2 text-center font-medium">{NOTIF_LABELS[t] || t}</th>)}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
          {tags.map(tag => (
            <tr key={tag}>
              <td className="px-4 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${ROLE_COLORS[tag] || 'bg-slate-100 text-slate-700'}`}>{tag}</span></td>
              {types.map(t => {
                const on = policy.policy[tag]?.[t];
                return <td key={t} className="px-4 py-2 text-center">{on ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <XCircle className="w-4 h-4 text-slate-300 inline" />}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Resolve preview ──
function ResolveView() {
  const [type, setType] = useState('power_outage');
  const [siteId, setSiteId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try { const r = await whatsappAPI.resolve(type, siteId || undefined); setData(r.data); }
    catch (e) { toast.error('Resolve failed'); }
    setLoading(false);
  }, [type, siteId]);
  useEffect(() => { run(); }, [run]);

  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="p-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <Eye className="w-4 h-4 text-slate-500" />
        <span className="text-xs text-slate-600">For notification</span>
        <select value={type} onChange={e => setType(e.target.value)} data-testid="resolve-type" className="text-xs border border-slate-200 rounded-sm px-2 py-1 bg-white">
          {Object.entries(NOTIF_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="text-xs text-slate-600 ml-2">site_id (optional)</span>
        <Input value={siteId} onChange={e => setSiteId(e.target.value)} className="h-7 text-xs w-64" placeholder="leave blank for global default" />
      </div>
      <div className="p-4">
        {loading ? <span className="text-xs text-slate-400">Resolving…</span> :
          data && data.recipients.length === 0 ? <span className="text-xs text-slate-400">No recipients match this notification.</span> :
          data && (
            <>
              <div className="text-[11px] text-slate-500 mb-2">{data.count} recipient(s):</div>
              <ul className="space-y-1">
                {data.recipients.map(r => (
                  <li key={r.contact_id} className="text-xs flex items-center gap-2">
                    <span className="font-medium">{r.name}</span>
                    <span className="font-mono text-slate-500">{r.phone_e164}</span>
                    {(r.role_tags || []).map(t => <span key={t} className={`text-[10px] px-1.5 py-0.5 rounded ${ROLE_COLORS[t]}`}>{t}</span>)}
                  </li>
                ))}
              </ul>
            </>
          )
        }
      </div>
    </div>
  );
}

// ─── Recent messages ──
function MessagesView({ rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <table className="w-full text-xs" data-testid="messages-table">
        <thead><tr className="bg-slate-50 text-slate-500">
          <th className="px-4 py-2 text-left font-medium w-24">Direction</th>
          <th className="px-4 py-2 text-left font-medium">Phone</th>
          <th className="px-4 py-2 text-left font-medium">Body</th>
          <th className="px-4 py-2 text-left font-medium w-28">Status</th>
          <th className="px-4 py-2 text-left font-medium w-40">Time</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rows.length === 0 ? (
            <tr><td colSpan={5} className="p-8 text-center text-xs text-slate-400">No messages yet.</td></tr>
          ) : rows.map(m => (
            <tr key={m.log_id || m.twilio_sid} className="hover:bg-slate-50/50">
              <td className="px-4 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${m.direction === 'inbound' ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-700'}`}>{m.direction}</span></td>
              <td className="px-4 py-2 font-mono text-[11px]">{m.direction === 'inbound' ? m.from : m.to}</td>
              <td className="px-4 py-2 text-[11px] text-slate-700 max-w-md truncate">{m.body}</td>
              <td className="px-4 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLORS[m.status] || 'bg-slate-100 text-slate-700'}`}>{m.status}</span>{m.error && <div className="text-[9px] text-red-600 truncate max-w-[120px]" title={m.error}>{m.error}</div>}</td>
              <td className="px-4 py-2 text-[11px] text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Contact upsert modal ──
function ContactModal({ initial, onClose, onSaved }) {
  const isNew = !initial?.contact_id;
  const [form, setForm] = useState({
    name: initial?.name || '',
    phone_e164: initial?.phone_e164 || '+91',
    role_tags: initial?.role_tags || [],
    is_active: initial?.is_active !== false,
    opt_in_status: initial?.opt_in_status !== false,
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const toggleTag = (t) => setForm(f => ({ ...f, role_tags: f.role_tags.includes(t) ? f.role_tags.filter(x => x !== t) : [...f.role_tags, t] }));
  const save = async () => {
    if (!form.name) return toast.error('Name required');
    if (!/^\+\d{10,15}$/.test(form.phone_e164)) return toast.error('Phone must be E.164 (e.g. +919900420136)');
    setSaving(true);
    try { await whatsappAPI.upsertContact(form); toast.success(isNew ? 'Contact added' : 'Contact updated'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{isNew ? 'Add Contact' : `Edit ${initial.name}`}</DialogTitle><DialogDescription>Internal-only. Never displayed publicly.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
          <div><Label className="text-xs">Phone (E.164, e.g. +919900420136)</Label><Input value={form.phone_e164} onChange={e => setForm({ ...form, phone_e164: e.target.value.replace(/\s/g, '') })} data-testid="contact-phone" className="font-mono" disabled={!isNew} /></div>
          <div><Label className="text-xs">Role tags</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {Object.keys(ROLE_COLORS).map(t => (
                <button key={t} onClick={() => toggleTag(t)} type="button" data-testid={`tag-${t}`}
                  className={`text-[11px] px-2 py-1 rounded border transition-colors ${form.role_tags.includes(t) ? `${ROLE_COLORS[t]} border-transparent` : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.opt_in_status} onChange={e => setForm({ ...form, opt_in_status: e.target.checked })} /> Opted-in for messages</label>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="save-contact-btn">{saving ? 'Saving…' : 'Save'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Send test alert modal ──
function SendTestModal({ contacts, onClose, onSent }) {
  const [type, setType] = useState('power_outage');
  const [picked, setPicked] = useState(new Set());
  const [custom, setCustom] = useState('');
  const [sending, setSending] = useState(false);
  const togglePicked = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pickAll = () => setPicked(new Set(contacts.filter(c => c.is_active && c.opt_in_status).map(c => c.contact_id)));
  const eligible = useMemo(() => contacts.filter(c => c.is_active && c.opt_in_status), [contacts]);

  const send = async () => {
    if (picked.size === 0) return toast.error('Pick at least one contact');
    setSending(true);
    try {
      const r = await whatsappAPI.sendTest({
        contact_ids: [...picked], notification_type: type,
        custom_message: custom.trim() || undefined,
      });
      const ok = r.data.results.filter(x => x.status !== 'failed' && x.status !== 'skipped').length;
      const fail = r.data.results.filter(x => x.status === 'failed').length;
      toast.success(`Sent ${ok} · Failed ${fail}`);
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Send Test Alert</DialogTitle><DialogDescription>Real WhatsApp send via Twilio. Sandbox-mode rules apply (recipients must have joined first).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Notification type (decides preview body)</Label>
            <select value={type} onChange={e => setType(e.target.value)} data-testid="test-type" className="w-full mt-1 text-xs border border-slate-200 rounded-sm px-2 py-1.5 bg-white">
              {Object.entries(NOTIF_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between"><Label className="text-xs">Recipients</Label>
            <button onClick={pickAll} className="text-[11px] text-slate-500 underline">Pick all eligible</button>
          </div>
          <div className="border border-slate-200 rounded-sm max-h-44 overflow-y-auto">
            {eligible.length === 0 ? <div className="p-3 text-xs text-slate-400">No active opted-in contacts.</div> :
              eligible.map(c => (
                <label key={c.contact_id} className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-slate-50 last:border-b-0 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={picked.has(c.contact_id)} onChange={() => togglePicked(c.contact_id)} data-testid={`pick-${c.contact_id}`} />
                  <span className="font-medium">{c.name}</span>
                  <span className="font-mono text-slate-500">{c.phone_e164}</span>
                  {(c.role_tags || []).map(t => <span key={t} className={`text-[9px] px-1 py-0.5 rounded ${ROLE_COLORS[t]}`}>{t}</span>)}
                </label>
              ))
            }
          </div>
          <div><Label className="text-xs">Custom message (optional — overrides preset)</Label>
            <textarea value={custom} onChange={e => setCustom(e.target.value)} rows={3} className="w-full text-xs border border-slate-200 rounded-sm p-2 font-mono" placeholder="Leave blank to use the preset for the chosen notification type" />
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={send} disabled={sending || picked.size === 0} data-testid="send-test-btn">{sending ? 'Sending…' : <><Send className="w-3.5 h-3.5 mr-1" /> Send to {picked.size}</>}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
